import { Image } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import { SxProps } from '@mui/system';
import clsx from 'clsx';
import {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { atom, useRecoilState } from 'recoil';
import { useMountedState } from '../utils';
import { loadImage } from '../utils/loadImage';
import { Fitting, useStyles } from './styles';

export interface ImageLoaderFallback {
  src: string | undefined | null;
  timeout?: number;
}

const failedSourcesAtom = atom<string[]>({
  key: 'failedSources',
  default: [],
});

interface ImageLoaderProps {
  className?: string;
  fallbacks?: ImageLoaderFallback[];
  image?: ReactNode;
  loaderSize?: number;
  src?: string | null;
  style?: CSSProperties;
  timeout?: number;
  fitting?: Fitting;
  sx?: SxProps;
}

export const ImageLoader: FC<ImageLoaderProps> = ({
  className,
  fallbacks = [],
  image,
  loaderSize = 30,
  src: mainSrc,
  style,
  timeout: mainTimeout,
  fitting = 'cover',
  sx,
}) => {
  const isMounted = useMountedState();
  const classes = useStyles({ fitting });
  const [failedSources, setFailedSources] = useRecoilState(failedSourcesAtom);
  const sources = useMemo(
    () =>
      [{ src: mainSrc, timeout: mainTimeout }, ...fallbacks].filter(
        ({ src }) => src && !failedSources.includes(src),
      ),
    [failedSources, fallbacks, mainSrc, mainTimeout],
  );
  const defaultSrc = useMemo(() => sources[0]?.src || undefined, [sources]);
  const [imgSrc, setImgSrc] = useState<string | undefined>(defaultSrc);
  const [loading, setLoading] = useState(true);

  const defaultImage = image || (
    <Image className={classes.imgIcon} style={{ fontSize: loaderSize * 2 }} />
  );

  useEffect(() => {
    setImgSrc(defaultSrc);
    setLoading(true);

    sources
      .reduce<Promise<string>>(
        (prev, { src, timeout }, i, array) =>
          prev.catch(() => {
            if (i > 0) {
              setImgSrc(undefined);
              const previousSrc = array[i - 1]?.src;
              previousSrc && setFailedSources((old) => [...old, previousSrc]);
            }
            return loadImage(src, timeout).then((img) => img.src);
          }),
        Promise.reject(),
      )
      .then((src) => {
        isMounted() && setImgSrc(src);
      })
      .catch(() => {
        isMounted() && setImgSrc(undefined);
      })
      .finally(() => {
        isMounted() && setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainSrc]);

  return (
    <Box className={clsx(classes.wrapper, className)} style={style} sx={sx}>
      {loading && (
        <div className={classes.center}>
          <CircularProgress size={loaderSize} />
        </div>
      )}
      {imgSrc ? (
        <img className={classes.image} src={imgSrc} draggable={false} />
      ) : (
        !loading && <div className={classes.center}>{defaultImage}</div>
      )}
    </Box>
  );
};
